import { useState, useCallback, useEffect, useRef } from "react";
import { useFaust } from "../../contexts/FaustContext";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
    ChevronDown,
    ChevronRight,
    FilePlus,
    FileText,
    FileCode,
    FileAudio,
    Library,
    Star,
    Pencil,
    Trash2,
    RefreshCw,
} from "lucide-react";
import { cn } from "../../lib/utils";

export function FileTree() {
    const { state, dispatch, libFaustRef, editorRef, compileOptionsRef, bfsRef, saveCompileOptions } = useFaust();
    const [expanded, setExpanded] = useState(true);
    const [files, setFiles] = useState<string[]>([]);
    const [selectedFile, setSelectedFile] = useState<string>("");
    const [mainFileIndex, setMainFileIndex] = useState(0);
    const [editingFile, setEditingFile] = useState<string | null>(null);
    const editInputRef = useRef<HTMLInputElement>(null);

    const PROJECT_DIR = "/usr/share/project/";

    // Load files from Emscripten FS
    const loadFiles = useCallback(() => {
        const libFaust = libFaustRef.current;
        if (!libFaust) return;
        const fs = libFaust.fs();
        try {
            const entries = fs.readdir(PROJECT_DIR) as string[];
            const fileList = entries.filter((n: string) => {
                if (n === "." || n === "..") return false;
                try {
                    return fs.isFile(fs.stat(PROJECT_DIR + n).mode);
                } catch {
                    return false;
                }
            });
            if (fileList.length === 0) {
                // Create default file
                fs.writeFile(PROJECT_DIR + "untitled.dsp", 'import("stdfaust.lib");\nprocess = _;');
                fileList.push("untitled.dsp");
            }
            setFiles(fileList);
            if (!selectedFile && fileList.length > 0) {
                selectFile(fileList[0], fs);
            }
        } catch {
            // FS not ready yet
        }
    }, [libFaustRef, selectedFile]);

    useEffect(() => {
        if (state.isReady) loadFiles();
    }, [state.isReady, loadFiles]);

    const isBinaryFile = (name: string) =>
        /\.(wav|flac|mp3|ogg|aiff?)$/i.test(name);

    const selectFile = useCallback(
        (name: string, fs?: any) => {
            const fileSystem = fs || libFaustRef.current?.fs();
            if (!fileSystem) return;
            setSelectedFile(name);
            dispatch({ type: "SET_CURRENT_FILE", name });
            // Only load text files into the editor
            if (!isBinaryFile(name)) {
                try {
                    const content = fileSystem.readFile(PROJECT_DIR + name, { encoding: "utf8" });
                    if (editorRef.current) {
                        editorRef.current.setValue(content);
                    }
                } catch {
                    // file read error
                }
            }
        },
        [libFaustRef, editorRef, dispatch]
    );

    const handleNewFile = useCallback(() => {
        const libFaust = libFaustRef.current;
        if (!libFaust) return;
        const fs = libFaust.fs();
        let name = "untitled.dsp";
        let i = 1;
        while (files.includes(name)) {
            name = `untitled${i}.dsp`;
            i++;
        }
        fs.writeFile(PROJECT_DIR + name, "");
        setFiles((prev) => [...prev, name]);
        selectFile(name, fs);
    }, [libFaustRef, files, selectFile]);

    const handleDelete = useCallback(
        (name: string) => {
            const libFaust = libFaustRef.current;
            if (!libFaust || files.length <= 1) return;
            const fs = libFaust.fs();
            try {
                fs.unlink(PROJECT_DIR + name);
                bfsRef.current?.unlink(name).catch(() => {});
            } catch {}
            const newFiles = files.filter((f) => f !== name);
            setFiles(newFiles);
            if (selectedFile === name && newFiles.length > 0) {
                selectFile(newFiles[0], fs);
            }
        },
        [libFaustRef, files, selectedFile, selectFile, bfsRef]
    );

    const handleRename = useCallback(
        (oldName: string, newName: string) => {
            const libFaust = libFaustRef.current;
            if (!libFaust || !newName || newName === oldName) {
                setEditingFile(null);
                return;
            }
            const fs = libFaust.fs();
            try {
                fs.rename(PROJECT_DIR + oldName, PROJECT_DIR + newName);
            } catch {}
            setFiles((prev) => prev.map((f) => (f === oldName ? newName : f)));
            if (selectedFile === oldName) {
                setSelectedFile(newName);
                dispatch({ type: "SET_CURRENT_FILE", name: newName });
            }
            setEditingFile(null);
        },
        [libFaustRef, selectedFile, dispatch]
    );

    const handleSetMain = useCallback(
        (index: number) => {
            setMainFileIndex(index);
            compileOptionsRef.current.mainFile = files[index];
            saveCompileOptions();
        },
        [files, compileOptionsRef, saveCompileOptions]
    );

    useEffect(() => {
        if (editingFile && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingFile]);

    return (
        <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-1">
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600 hover:text-zinc-400 transition-colors"
                >
                    {expanded ? (
                        <ChevronDown className="h-3 w-3" />
                    ) : (
                        <ChevronRight className="h-3 w-3" />
                    )}
                    Files
                </button>
                <div className="flex items-center gap-0.5">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-zinc-600 hover:text-zinc-300"
                            onClick={loadFiles}
                        >
                            <RefreshCw className="h-3 w-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Refresh</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-zinc-600 hover:text-zinc-300"
                            onClick={handleNewFile}
                        >
                            <FilePlus className="h-3 w-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">New file</TooltipContent>
                </Tooltip>
                </div>
            </div>

            {/* File list */}
            {expanded && (
                <div className="flex flex-col gap-px">
                    {files.map((file, index) => (
                        <div
                            key={file}
                            className={cn(
                                "file-item group",
                                selectedFile === file && "active",
                                index === mainFileIndex && "main-file"
                            )}
                            onClick={() => selectFile(file)}
                        >
                            {file.endsWith(".lib") ? (
                                <Library className="h-3 w-3 shrink-0 text-amber-500" />
                            ) : file.endsWith(".dsp") ? (
                                <FileCode className="h-3 w-3 shrink-0" />
                            ) : isBinaryFile(file) ? (
                                <FileAudio className="h-3 w-3 shrink-0 text-purple-400" />
                            ) : (
                                <FileText className="h-3 w-3 shrink-0" />
                            )}

                            {editingFile === file ? (
                                <input
                                    ref={editInputRef}
                                    defaultValue={file}
                                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-1 py-0 text-xs text-zinc-100 outline-none"
                                    onBlur={(e) => handleRename(file, e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleRename(file, (e.target as HTMLInputElement).value);
                                        if (e.key === "Escape") setEditingFile(null);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            ) : (
                                <span className="flex-1 truncate text-xs">{file}</span>
                            )}

                            {/* Action buttons (visible on hover) */}
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                {index !== mainFileIndex && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="p-0.5 text-zinc-600 hover:text-yellow-400"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSetMain(index);
                                                }}
                                            >
                                                <Star className="h-2.5 w-2.5" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Set as main</TooltipContent>
                                    </Tooltip>
                                )}
                                <button
                                    className="p-0.5 text-zinc-600 hover:text-zinc-300"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingFile(file);
                                    }}
                                >
                                    <Pencil className="h-2.5 w-2.5" />
                                </button>
                                {files.length > 1 && (
                                    <button
                                        className="p-0.5 text-zinc-600 hover:text-red-400"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(file);
                                        }}
                                    >
                                        <Trash2 className="h-2.5 w-2.5" />
                                    </button>
                                )}
                            </div>

                            {/* Main file indicator */}
                            {index === mainFileIndex && (
                                <Star className="h-2.5 w-2.5 text-[#0078d4] fill-[#0078d4] shrink-0" />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
